/**
 * @fileoverview Double confirmation modal for deleting a budget.
 * Requires two confirmations to prevent accidental deletion.
 */

import { useState } from 'react';
import styles from './BudgetManagement.module.css';

/**
 * DeleteBudgetConfirmation Component
 *
 * Two-stage confirmation modal for irreversible budget deletion.
 *
 * Props:
 * - isOpen (boolean): Whether modal is visible
 * - budgetKey (string): Key of budget being deleted
 * - budgetName (string): Name of budget being deleted
 * - onClose (function): Called when modal should close
 * - onConfirmDelete (function): Called when final delete is confirmed
 */
function DeleteBudgetConfirmation({ isOpen, budgetKey, budgetName, onClose, onConfirmDelete }) {
    const [confirmationStage, setConfirmationStage] = useState(1);

    if (!isOpen) return null;

    const handleFirstConfirm = () => {
        setConfirmationStage(2);
    };

    const handleFinalConfirm = () => {
        onConfirmDelete(budgetKey);
        setConfirmationStage(1); // Reset for next time
    };

    const handleClose = () => {
        setConfirmationStage(1); // Reset stage
        onClose();
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <button
                    onClick={handleClose}
                    className={styles.closeButton}
                    aria-label="Close"
                >
                    ×
                </button>

                {confirmationStage === 1 ? (
                    // First confirmation
                    <>
                        <h2 className={styles.modalTitle}>Delete Budget?</h2>
                        <p className={styles.warningText}>
                            Do you want to delete the budget <strong>"{budgetName}"</strong>?
                        </p>
                        <p className={styles.warningText}>
                            This action is <strong>irreversible</strong>.
                        </p>

                        <div className={styles.buttonGroup}>
                            <button
                                onClick={handleClose}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleFirstConfirm}
                                className={styles.deleteButton}
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </>
                ) : (
                    // Second confirmation
                    <>
                        <h2 className={styles.modalTitle}>Are You Sure?</h2>
                        <p className={styles.dangerText}>
                            <strong>WARNING:</strong> Deleting this budget cannot be undone.
                        </p>
                        <p className={styles.warningText}>
                            All transactions, categories, and data for <strong>"{budgetName}"</strong> will be permanently deleted.
                        </p>
                        <p className={styles.warningText}>
                            Are you absolutely sure you want to proceed?
                        </p>

                        <div className={styles.buttonGroup}>
                            <button
                                onClick={handleClose}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleFinalConfirm}
                                className={styles.deleteButton}
                            >
                                Yes, Permanently Delete
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default DeleteBudgetConfirmation;
